import assert from 'minimalistic-assert';
import lodash from 'lodash';
import { ModelIdSymbol } from '../constants.js';
import { createIsInstance, omitUndefined } from '../utils.js';
const { isEqual, set } = lodash;
const coerceToObject = (obj) => obj instanceof PersonalNote ? obj.toJSON() : obj;
const cleanUpNoteJSON = ({ personalNote, to }) => {
    delete personalNote.message;
    if (personalNote.sends) {
        Object.keys(personalNote.sends || Object.create(null)).forEach((address) => {
            const lower = address.toLowerCase();
            if (address !== lower && lower === to) {
                set(personalNote, ['sends', address, 'message'], null);
            }
        });
    }
};
export default class PersonalNote {
    txId;
    message;
    sends;
    constructor(props = Object.create(null)) {
        assert(typeof props.txId === 'string', `expected "txId", received: ${props.txId}`);
        Object.assign(this, props);
        this.txId = props.txId;
        this.message = props.message || '';
    }
    static get [ModelIdSymbol]() {
        return 'PersonalNote';
    }
    static isInstance = createIsInstance(PersonalNote);
    static [Symbol.hasInstance](instance) {
        return this.isInstance(instance);
    }
    static fromJSON(json) {
        return new PersonalNote(typeof json === 'string' ? JSON.parse(json) : json);
    }
    toJSON() {
        const obj = { ...this };
        if (this.sends) {
            obj.sends = Object.create(null);
            for (const [key, value] of Object.entries(this.sends)) {
                obj.sends[key] = { ...value };
            }
        }
        return omitUndefined(obj);
    }
    toRedactedJSON() {
        return {
            txId: this.txId,
        };
    }
    toString() {
        return String(this.txId);
    }
    equals(note) {
        if (!note) {
            return false;
        }
        return this === note || isEqual(this.toJSON(), note.toJSON());
    }
    update(fields) {
        const object = coerceToObject(fields);
        if (object.txId && object.txId !== this.txId) {
            throw new Error('"txId" cannot be changed once it is set');
        }
        const current = this.toJSON();
        const isNoop = Object.keys(object).every((field) => isEqual(current[field], object[field]));
        if (isNoop) {
            return this;
        }
        return PersonalNote.fromJSON({ ...current, ...object });
    }
    getMessage({ to } = Object.create(null)) {
        if (to && this.sends) {
            const sends = Object.entries(this.sends);
            for (const [address, { message }] of sends) {
                if (address.toLowerCase() === to.toLowerCase() && message) {
                    return message;
                }
            }
        }
        return this.message || '';
    }
    setMessage({ to, message }) {
        assert(typeof message === 'string', 'expected string "message"');
        if (!to)
            return this.update({ message });
        const json = this.toJSON();
        cleanUpNoteJSON({ personalNote: json, to });
        const update = set(json, ['sends', to, 'message'], message);
        return this.update(update);
    }
}
